import { useContext, Suspense, lazy } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import AdminContentLayout from "../../layouts/AdminContentLayout";
import DetailRenderer from "../../components/global/forms/DetailRenderer";
import CustomText from "../../components/atomic/CustomText";
import Button from "../../components/organism/Button";
import Banner from "../../components/atomic/Banner";
import TableLoadingFallback from "../../components/global/LoadingIndicators/TableLoadingFallback";
import CreatePatientPopup from "../../components/popups/createPatientPopup";
import OrganizationAndClinicianSelection from "../../components/popups/organizationAndClinicianSelectionPopup";
import usePatientDetail from "../../services/utils/custom hooks/Patients/usePatientDetail";
import useStateAndCity from "../../services/utils/custom hooks/useStateAndCity";
import useFeatureFlags from "../../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import useProfile from "../../services/utils/custom hooks/useProfile";
import { PopUpContext } from "../../services/context/popupContext";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import dataConstants from "../../constants/dataConstants";
import PatientInfoModel from "../../services/models/formModels/patientInfo.model";
import useOrganizations from "../../services/utils/custom hooks/useOrganizations";

// Lazy load heavy table component
const TestOrdersTable = lazy(() => import("../../components/Tables/TestOrdersTable"));

function PatientDetailPage(): JSX.Element {
  const history = useHistory();
  const oktaAuth = useOktaAuth();
  const { id }: any = useParams();
  const { data, isLoading } = usePatientDetail(id);
  const {
    state: { selectedOrganization, selectedPhysician, selectedPathLab },
  } = useContext(ApplicationState);
  const { data: orgs, isLoading: isLoadingOrgs } = useOrganizations();

  const { data: states } = useStateAndCity();
  const { visible, setVisible, setPopUp, setPopUpTitle } =
    useContext(PopUpContext);
  const { getActionsForSection } = useFeatureFlags();
  const { data: profile, isLoading: fetchingProfile } = useProfile();
  const roles = profile?.user?.roles ?? [];
  const isPLUPhysician = roles.includes("Physician") && roles.includes("PathLab User");
  const isPhysicianOnly = (roles.length === 1 && roles.includes("Physician")) || isPLUPhysician;

  function editPatient() {
    setVisible(!visible);
    setPopUpTitle(`Update ${data?.data.firstName}`);
    setPopUp(
      <CreatePatientPopup
        oktaAuth={oktaAuth}
        initialStep={data?.data.isEditable === false && (data.data.ssn === '' || data.data.mrn === '') ? 3 : undefined}
        patientInformation={{
          id: data?.data.id,
          reference: data?.data.organizationId ?? "",
          firstName: data?.data.firstName ?? "",
          lastName: data?.data.lastName ?? "",
          middleInitial: data?.data.middleInitial ?? "",
          birthDate: data?.data.birthDate ?? "",
          securityInfo: {
            ssn: data?.data.ssn ?? "",
            mrn: data?.data.mrn ?? "",
          },
          gender: data?.data.gender ?? "",
          state: data?.data.state ?? "",
          city: data?.data.city ?? "",
          phone: data?.data.phone ?? "",
          address: data?.data.address ?? "",
          email: data?.data.email ?? "",
          zipcode: data?.data.zipcode ?? "",
        }}
      />
    );
  }

  function PersonalInfo() {
    return (
      <div>
        <div className="flex justify-between items-center">
          <CustomText
            text={"Personal Info"}
            fontWeight="font-semibold"
            className="my-2 mt-5"
          />
          <Button
            textInformation={{
              text: "edit",
              fontWeight: "font-semibold",
            }}
            loading={false}
            disabled={isLoading || data?.data.isEditable === false && !(data.data.ssn === '' || data.data.mrn === '')}
            onClick={editPatient}
            buttonTheme="secondary"
            className=" mt-3 bg-white py-1.5"
          />
        </div>
        <DetailRenderer
          data={{
            firstName: data?.data.firstName,
            middleInitial: data?.data.middleInitial,
            lastName: data?.data.lastName,
            dateOfBirth: data?.data.birthDate,
            gender: data?.data.gender,
          }}
          groupFields={[
            "firstName",
            "middleInitial",
            "lastName",
            "dateOfBirth",
            "gender",
          ]}
          hiddenFields={[]}
        />
      </div>
    );
  }
  function ContactInfo() {
    return (
      <div>
        <div className="flex justify-between items-center">
          <CustomText
            text={"Contact Info"}
            fontWeight="font-semibold"
            className="my-2 mt-5"
          />
        </div>
        <DetailRenderer
          data={{
            email: data?.data.email,
            address: data?.data.address,
            city: data?.data.city,
            state: !!data?.data?.state
              ? states?.find(
                (state: any) => state.state_code === data?.data.state
              )?.name ?? data.data.state
              : "",
            phone: data?.data.phone,
            zipcode: data?.data.zipcode,
          }}
          groupFields={[
            "email",
            "address",
            "city",
            "state",
            "phone",
            "zipcode",
          ]}
          hiddenFields={[]}
        />
      </div>
    );
  }
  function SecurityInfo() {
    return (
      <div>
        <div className="flex justify-between items-center">
          <CustomText
            text={"Security"}
            fontWeight="font-semibold"
            className="my-2 mt-5"
          />
        </div>
        <DetailRenderer
          data={{
            ssn: data?.data?.ssn,
            mrn: data?.data.mrn,
          }}
          groupFields={["ssn", "mrn"]}
          hiddenFields={[]}
        />
      </div>
    );
  }
  return (
    <AdminContentLayout
      pageTitle={`Patient Detail | CSTL`}
      loading={isLoading}
      subHeaderTitle={`${data?.data.firstName} ${data?.data.lastName}`}
      redirect={history.goBack}
      customButtons={[
        {
          textInformation: {
            text: `New online order ${!isLoading ? "for " : ""} ${!!data?.data?.firstName ? data?.data?.firstName : ""
              } ${!!data?.data?.lastName ? data?.data?.lastName : ""}`,
            showLoadingIndication: isLoading,
            fontWeight: "font-semibold",
          },
          buttonTheme: "primary",
          disabled: isLoading ,
          loading: false,
          className: "mx-5",
          onClick: () => {
            const formattedBirthDate =
              data?.data?.birthDate?.split?.("-") ?? [];

            const patientInformation: PatientInfoModel = {
              id,
              firstName: data?.data?.firstName ?? "",
              lastName: data?.data?.lastName ?? "",
              middleInitial: data?.data?.middleInitial ?? "",
              birthDate: `${formattedBirthDate[1]}-${formattedBirthDate[2]}-${formattedBirthDate[0]}`,
              securityInfo: {
                ssn: data?.data?.ssn ?? "",
                mrn: data?.data?.mrn ?? "",
              },
              gender: data?.data?.gender ?? "",
              phone: data?.data?.phone ?? "",
              address: data?.data?.address ?? "",
              city: data?.data?.city ?? "",
              state: data?.data?.state ?? "",
              email: data?.data?.email ?? "",
              zipcode: data?.data?.zipcode ?? "",
            };
            const missingContextValues =
              !selectedOrganization ||
              !selectedPhysician ||
              (!selectedPathLab &&
                roles?.includes("Collaborative Order Agreement"));
            if (missingContextValues) {
              const patientOrgIdForPopup = orgs?.data?.find(
                (o: any) => o.fhirOrganizationId === data?.data?.organizationId
              )?.id;

              setVisible(!visible);
              setPopUpTitle(
                `Select ${roles?.includes("Collaborative Order Agreement")
                  ? "a PathLab"
                  : ""
                } an organization ${isPhysicianOnly ? "" : "and a clinician"}`
              );
              setPopUp(
                <OrganizationAndClinicianSelection
                  isPatientActionsCell
                  patientId={id}
                  patientOrganizationId={patientOrgIdForPopup}
                  handleNext={(newPatientId?: string) => {
                    const orderPatientInfo =
                      newPatientId != null
                        ? { ...patientInformation, id: newPatientId }
                        : patientInformation;
                    if (newPatientId != null) {
                      sessionStorage.setItem(
                        "orderDuplicatePatientId",
                        newPatientId
                      );
                    }
                    history.push(`/access/online-order`, {
                      patientInformation: JSON.stringify(orderPatientInfo),
                    });
                    setVisible(false);
                  }}
                />
              );
            } else
              history.push(`/access/online-order`, {
                patientInformation: JSON.stringify(patientInformation),
              });
          },
        },
      ]}
    >
      {!data?.data.isEditable && (
        <div className="md:px-5 px-3 min-w-12 overflow-auto">
          <Banner
            loading={isLoading}
            text={
              (data?.data.ssn === '' || data?.data.mrn === '') ? 'Only the SSN and MRN can be added via the portal. All other patient information cannot be modified once one or more orders have been submitted to Castle. To update other details, please contact the Castle team.' :
                "The patient's information cannot be updated via the portal as one or more of their orders have been submitted to Castle. To update the patient's details, please contact the Castle team."
            }
            type="info"
            hideButton={false}
          />
        </div>
      )}
      <div className="md:p-5 px-3 py-5 overflow-auto">
        <div className="bg-white border shadow-sm px-6 rounded-[16px] ">
          <PersonalInfo />
          <ContactInfo />
          <SecurityInfo />
        </div>
        <div className="overflow-auto lg:max-w-[calc(100vw_-_150px)]">
          <CustomText
            text="Test History"
            fontWeight="font-semibold"
            fontSize=""
            className="mt-5 mb-2"
          />
          <Suspense fallback={<TableLoadingFallback />}>
            <TestOrdersTable patientDetail={{ id }} />
          </Suspense>
        </div>
      </div>
    </AdminContentLayout>
  );
}
export default PatientDetailPage;
